import { getCriticalScanRatio, toNumber } from "@Containers/shared/progress/utils";
import MetricEventTypes from "@Enums/MetricEventTypes";
import ScoreFieldTypes from "@Enums/ScoreFieldTypes";
import {
  BybIqModel,
  GameplayDurationModel,
  MetricDefinition,
  MetricModel,
  NormalizedMetrics,
} from "@Helpers/types";
import type { UserStats } from "@Shared/schemas/stats/statsSchemas";
import { computeScanRateFromScanScore, DEFAULT_SCAN_FREQUENCY_WEIGHT } from "@Shared/utilities";

const formatMetricDisplayValue = (metricType: MetricEventTypes, value: number): number => {
  switch (metricType) {
    case MetricEventTypes.SCAN_FREQUENCY:
    case MetricEventTypes.CRITICAL_SCAN:
      return Number(value.toFixed(2));
    case MetricEventTypes.SCAN_TIMING:
      return Number(value.toFixed(1));
    case MetricEventTypes.AWARENESS:
    case MetricEventTypes.DECISION:
    default:
      return Number(value.toFixed(0));
  }
};

export const normalizeBybIq = (value?: number): BybIqModel => ({
  displayValue: Number(value?.toFixed(0) || 0),
  unitKey: "metric.byb_iq.long",
});

export const normalizeGameplayDuration = (value: number): GameplayDurationModel => {
  const rawValue = Math.max(toNumber(value), 0);

  return {
    displayValue: rawValue < 60 ? Number(rawValue.toFixed(0)) : Number((rawValue / 60).toFixed(0)),
    unitKey: rawValue < 60 ? "metric.gameplay_duration.seconds.short" : "metric.gameplay_duration.minutes.short"
  };
};

const metricDefinitions: Record<MetricEventTypes, MetricDefinition> = {
  [MetricEventTypes.SCAN_FREQUENCY]: {
    eventType: MetricEventTypes.SCAN_FREQUENCY,
    scoreField: ScoreFieldTypes.SCAN_SCORE,
    unitKey: "metric.scan_rate.short",
    normalize: stats => {
      const scanScore = toNumber(stats.scan_score);

      return {
        displayValue: computeScanRateFromScanScore(scanScore),
        progressValue: scanScore * DEFAULT_SCAN_FREQUENCY_WEIGHT,
        radarValue: scanScore,
      };
    },
  },
  [MetricEventTypes.SCAN_TIMING]: {
    eventType: MetricEventTypes.SCAN_TIMING,
    scoreField: ScoreFieldTypes.SCAN_TIMING,
    unitKey: "metric.scan_timing.short",
    normalize: stats => {
      const scanTiming = toNumber(stats.scan_timing);

      return {
        displayValue: scanTiming,
        progressValue: scanTiming,
        radarValue: scanTiming,
      };
    },
  },
  [MetricEventTypes.CRITICAL_SCAN]: {
    eventType: MetricEventTypes.CRITICAL_SCAN,
    scoreField: ScoreFieldTypes.CRITICAL_SCANS,
    unitKey: "metric.critical_scan.short",
    normalize: stats => {
      const criticalScanRatio = getCriticalScanRatio(stats);

      return {
        displayValue: criticalScanRatio,
        progressValue: criticalScanRatio * 100,
        radarValue: criticalScanRatio * 100,
      };
    },
  },
  [MetricEventTypes.AWARENESS]: {
    eventType: MetricEventTypes.AWARENESS,
    scoreField: ScoreFieldTypes.AWARENESS_SCORE,
    unitKey: "metric.awareness.short",
    normalize: stats => {
      const awarenessScore = toNumber(stats.awareness_score);

      return {
        displayValue: awarenessScore,
        progressValue: awarenessScore,
        radarValue: awarenessScore,
      };
    },
  },
  [MetricEventTypes.DECISION]: {
    eventType: MetricEventTypes.DECISION,
    scoreField: ScoreFieldTypes.PASSES_SCORE,
    unitKey: "metric.passes_score.short",
    normalize: stats => {
      const passesScore = toNumber(stats.passes_score);

      return {
        displayValue: passesScore,
        progressValue: passesScore,
        radarValue: passesScore,
      };
    },
  },
};

export const normalizeMetric = (stats: UserStats, metricType: MetricEventTypes): MetricModel => {
  const metricDefinition = metricDefinitions[metricType];
  const normalizedMetric = metricDefinition.normalize(stats);

  return {
    eventType: metricDefinition.eventType,
    scoreField: metricDefinition.scoreField,
    unitKey: metricDefinition.unitKey,
    ...normalizedMetric,
    displayValue: formatMetricDisplayValue(metricType, normalizedMetric.displayValue)
  };
};

export const normalizeScanFrequencyScore = (score: number): MetricModel => {
  return normalizeMetric({ scan_score: score } as UserStats, MetricEventTypes.SCAN_FREQUENCY);
};

export const normalizeMetrics = (stats: UserStats): NormalizedMetrics => {
  return {
    [MetricEventTypes.SCAN_FREQUENCY]: normalizeMetric(stats, MetricEventTypes.SCAN_FREQUENCY),
    [MetricEventTypes.SCAN_TIMING]: normalizeMetric(stats, MetricEventTypes.SCAN_TIMING),
    [MetricEventTypes.CRITICAL_SCAN]: normalizeMetric(stats, MetricEventTypes.CRITICAL_SCAN),
    [MetricEventTypes.AWARENESS]: normalizeMetric(stats, MetricEventTypes.AWARENESS),
    [MetricEventTypes.DECISION]: normalizeMetric(stats, MetricEventTypes.DECISION),
  };
};
